/**
 * React.lazy for code-split routes, hardened against Vite's preload-error contract.
 *
 * THE BUG THIS EXISTS TO FIX
 *   Vite rewrites `lazy(() => import("@/pages/AdminCaptures"))` into
 *   `lazy(() => __vitePreload(() => import("./AdminCaptures-HASH.js"), deps))`.
 *   The tail of __vitePreload is, near enough:
 *
 *       .then(() => baseModule())
 *       .catch((err) => {
 *         const e = new Event("vite:preloadError", { cancelable: true });
 *         e.payload = err;
 *         window.dispatchEvent(e);
 *         if (!e.defaultPrevented) throw err;
 *       })
 *
 *   main.tsx listens for that event and calls preventDefault() once a
 *   stale-chunk reload is under way, precisely so Vite does NOT rethrow. But a
 *   catch handler that does not rethrow *resolves* — with undefined. So the
 *   lazy factory hands React `undefined`, React's lazyInitializer runs
 *   `return moduleObject.default`, and the tab throws
 *
 *       TypeError: Cannot read properties of undefined (reading 'default')
 *
 *   into the boundary, which files it to /api/errors. That is the opposite of
 *   what preventDefault was for: it converted a silent, self-healing reload
 *   into a crash card plus an error report, on every admin route in turn
 *   (analytics, deletions, events, captures — see error_log, Aug 2026).
 *
 *   window.location.reload() is asynchronous, which is what leaves React the
 *   window to render the route and throw before the document goes away.
 *
 * THE FIX
 *   undefined here does not mean "module missing", it means "a reload is
 *   already happening". So do not resolve: hand React a promise that never
 *   settles, and Suspense simply keeps its fallback up for the fraction of a
 *   second until the document is replaced. Nothing renders, nothing throws,
 *   nothing is reported.
 *
 *   The grace period is the escape hatch. If the reload has not taken effect
 *   after it, reloading evidently did not fix this, so we surface a real error
 *   rather than spin forever — the same judgement staleChunkReload's own
 *   RELOAD_WINDOW_MS guard makes.
 */
import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

/* React's own lazy() is typed against ComponentType<any>; mirror it exactly so
   lazyRoute stays a drop-in replacement rather than narrowing what a route may
   be. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

/**
 * How long to hold the Suspense fallback waiting for the reload to land.
 * Generous: this only has to outlast the browser tearing the document down.
 */
export const RELOAD_GRACE_MS = 10_000;

/** A module namespace object is always an object, so undefined is unambiguous. */
type LoadedModule<T> = { default: T } | undefined;

export interface LazyRouteDeps {
  /** Injectable for tests; real code just waits. */
  delay?: (ms: number) => Promise<void>;
}

/**
 * The whole decision, split out so it is testable without rendering a tree.
 *
 * Resolves with the module when there is one. When there is not, waits out the
 * grace period and then throws — never resolves with something React would
 * read `.default` off.
 */
export async function resolveRouteModule<T>(
  loaded: LoadedModule<T>,
  deps: LazyRouteDeps = {},
): Promise<{ default: T }> {
  if (loaded) return loaded;

  const delay = deps.delay ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  await delay(RELOAD_GRACE_MS);

  // Still here: the document was never replaced, so the reload did not happen
  // or did not help. Let this reach the boundary, where staleChunkReload's
  // guard will decline a second reload and the error gets reported for real.
  throw new Error(
    "Route chunk unavailable: a stale-chunk reload was started but never completed",
  );
}

/**
 * Drop-in replacement for React.lazy for every code-split route.
 *
 * Use this rather than lazy() directly, so a deploy landing under an open tab
 * stays a silent reload instead of a crash card.
 */
export function lazyRoute<T extends AnyComponent>(
  factory: () => Promise<{ default: T }>,
  deps: LazyRouteDeps = {},
): LazyExoticComponent<T> {
  return lazy(async () => {
    // The cast is the point: the type says this always resolves to a module,
    // and __vitePreload is exactly the case where that is a lie.
    const loaded = (await factory()) as LoadedModule<T>;
    return resolveRouteModule(loaded, deps);
  });
}
